import axios from "axios";

// const baseURL = 'https://localhost:44342/api/';
const baseURL = 'https://qa.pharmacy.api.sehhasolutions.com/api/'; //prod
;

/** Base URL for the API server (no /api) - use for static files e.g. /uploads/products/xxx.jpg */
// export const API_BASE_ORIGIN = 'https://localhost:44342';
export const API_BASE_ORIGIN = 'https://qa.pharmacy.api.sehhasolutions.com'; //prod
;


/** Resolve product image URL (relative paths from API uploads become absolute). */
export function getProductImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const base = API_BASE_ORIGIN;
    return url.startsWith('/') ? base + url : base + '/' + url;
}

// Public API (no auth required)
const publicAPI = axios.create({
    baseURL,
});

// Authenticated API (with token)
const authenticatedAPI = axios.create({
    baseURL,
});

// Add request interceptor for authenticated API
authenticatedAPI.interceptors.request.use(
    (config) => {
        const token = sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
const setupResponseInterceptor = (apiInstance) => {
    apiInstance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === 401) {
                sessionStorage.removeItem('token');
                sessionStorage.removeItem('user');
                // Redirect to admin login for admin routes, customer login for customer routes
                const url = error.config?.url || '';
                if (url.includes('/admin/') || url.includes('/customer/auth')) {
                    window.location.href = '/admin-login';
                } else {
                    window.location.href = '/customer/login';
                }
            }
            return Promise.reject(error);
        }
    );
};

setupResponseInterceptor(publicAPI);
setupResponseInterceptor(authenticatedAPI);

// ==================== PUBLIC PRODUCTS API ====================
export const PublicProductsAPI = {
    getAllProducts: async (organizationId = 1, page = 1, pageSize = 20, categorySlug = null) => {
        try {
            let url = `public/products?organizationId=${organizationId}&page=${page}&pageSize=${pageSize}`;
            if (categorySlug) url += `&categorySlug=${categorySlug}`;
            const response = await publicAPI.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching products:", error);
            throw error;
        }
    },

    getProductById: async (id) => {
        try {
            const response = await publicAPI.get(`public/products/${id}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching product:", error);
            throw error;
        }
    },

    getProductBySlug: async (slug, organizationId = 1) => {
        try {
            const response = await publicAPI.get(`public/products/slug/${slug}?organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching product by slug:", error);
            throw error;
        }
    },

    searchProducts: async (query, organizationId = 1, page = 1, pageSize = 20) => {
        try {
            const response = await publicAPI.get(`public/products/search?q=${encodeURIComponent(query)}&organizationId=${organizationId}&page=${page}&pageSize=${pageSize}`);
            return response.data;
        } catch (error) {
            console.error("Error searching products:", error);
            throw error;
        }
    },

    getProductsByCategory: async (categoryId, organizationId = 1) => {
        try {
            const response = await publicAPI.get(`public/products/category/${categoryId}?organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching products by category:", error);
            throw error;
        }
    },

    getAllCategories: async (organizationId = 1) => {
        try {
            const response = await publicAPI.get(`public/products/categories?organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching categories:", error);
            throw error;
        }
    },

    getCategoryBySlug: async (slug, organizationId = 1) => {
        try {
            const response = await publicAPI.get(`public/products/categories/slug/${slug}?organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching category by slug:", error);
            throw error;
        }
    },
};

// ==================== PUBLIC BANNERS API ====================
export const PublicBannersAPI = {
    getBanners: async (organizationId = 1) => {
        try {
            const response = await publicAPI.get(`public/banners?organizationId=${organizationId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching banners:", error);
            throw error;
        }
    }
};

// ==================== ADMIN BANNERS API ====================
export const AdminBannersAPI = {
    getAll: async () => {
        try {
            const response = await authenticatedAPI.get('admin/banners');
            return response.data;
        } catch (error) {
            console.error("Error fetching admin banners:", error);
            throw error;
        }
    },
    create: async (banner) => {
        try {
            const response = await authenticatedAPI.post('admin/banners', banner);
            return response.data;
        } catch (error) {
            console.error("Error creating banner:", error);
            throw error;
        }
    },
    update: async (id, banner) => {
        try {
            const response = await authenticatedAPI.put(`admin/banners/${id}`, banner);
            return response.data;
        } catch (error) {
            console.error("Error updating banner:", error);
            throw error;
        }
    },
    remove: async (id) => {
        try {
            await authenticatedAPI.delete(`admin/banners/${id}`);
            return true;
        } catch (error) {
            console.error("Error deleting banner:", error);
            throw error;
        }
    },
    uploadImage: async (file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await authenticatedAPI.post('admin/banners/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error("Error uploading banner image:", error);
            throw error;
        }
    }
};

// ==================== PUBLIC REVIEWS API ====================
export const PublicReviewsAPI = {
    getForProduct: async (productId) => {
        try {
            const response = await publicAPI.get(`public/products/${productId}/reviews`);
            return response.data;
        } catch (error) {
            console.error("Error fetching reviews:", error);
            throw error;
        }
    },
    createForProduct: async (productId, payload) => {
        try {
            const response = await publicAPI.post(`public/products/${productId}/reviews`, payload);
            return response.data;
        } catch (error) {
            console.error("Error submitting review:", error);
            throw error;
        }
    }
};

// ==================== ADMIN REVIEWS API ====================
export const AdminReviewsAPI = {
    getAll: async ({ approved = null, productId = null } = {}) => {
        try {
            const qs = [];
            if (approved !== null && approved !== undefined) qs.push(`approved=${approved}`);
            if (productId !== null && productId !== undefined) qs.push(`productId=${productId}`);
            const url = `admin/reviews${qs.length ? `?${qs.join('&')}` : ''}`;
            const response = await authenticatedAPI.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching admin reviews:", error);
            throw error;
        }
    },
    setApproval: async (id, isApproved) => {
        try {
            const response = await authenticatedAPI.put(`admin/reviews/${id}/approval`, { isApproved });
            return response.data;
        } catch (error) {
            console.error("Error updating review approval:", error);
            throw error;
        }
    },
    remove: async (id) => {
        try {
            await authenticatedAPI.delete(`admin/reviews/${id}`);
            return true;
        } catch (error) {
            console.error("Error deleting review:", error);
            throw error;
        }
    }
};

// ==================== PUBLIC CART API ====================
// Backend returns: createCart => { cartID, sessionID }; getCart => { cartID, sessionID, createdDate, updatedDate, cartItems: [...] }
export const PublicCartAPI = {
    createCart: async () => {
        try {
            const response = await publicAPI.post('public/cart');
            const data = response?.data;
            return data;
        } catch (error) {
            console.error("Error creating cart:", error);
            throw error;
        }
    },

    getCart: async (sessionId) => {
        try {
            const safeId = encodeURIComponent(String(sessionId).trim());
            const response = await publicAPI.get(`public/cart/${safeId}`);
            const data = response?.data;
            return data;
        } catch (error) {
            console.error("Error fetching cart:", error);
            throw error;
        }
    },

    addItem: async (sessionId, productId, quantity) => {
        try {
            const safeId = encodeURIComponent(String(sessionId).trim());
            const response = await publicAPI.post(`public/cart/${safeId}/items`, {
                productID: productId,
                quantity: quantity
            });
            return response.data;
        } catch (error) {
            console.error("Error adding item to cart:", error);
            throw error;
        }
    },

    updateItem: async (itemId, quantity) => {
        try {
            const response = await publicAPI.put(`public/cart/items/${itemId}`, {
                quantity: quantity
            });
            return response.data;
        } catch (error) {
            console.error("Error updating cart item:", error);
            throw error;
        }
    },

    removeItem: async (itemId) => {
        try {
            await publicAPI.delete(`public/cart/items/${itemId}`);
            return true;
        } catch (error) {
            console.error("Error removing cart item:", error);
            throw error;
        }
    },

    getCartTotal: async (sessionId) => {
        try {
            const response = await publicAPI.get(`public/cart/${sessionId}/total`);
            return response.data;
        } catch (error) {
            console.error("Error fetching cart total:", error);
            throw error;
        }
    },

    clearCart: async (sessionId) => {
        try {
            await publicAPI.delete(`public/cart/${sessionId}`);
            return true;
        } catch (error) {
            console.error("Error clearing cart:", error);
            throw error;
        }
    },

    /** Guest checkout: create order from guest cart (no login required) */
    guestCheckout: async (sessionId, orderData) => {
        try {
            const response = await publicAPI.post(`public/cart/${sessionId}/checkout`, orderData);
            return response.data;
        } catch (error) {
            console.error("Error during guest checkout:", error);
            throw error;
        }
    },
};

// ==================== CUSTOMER AUTH API ====================
export const CustomerAuthAPI = {
    register: async (userData) => {
        try {
            const response = await authenticatedAPI.post('customer/auth/register', userData);
            return response.data;
        } catch (error) {
            console.error("Error registering customer:", error);
            throw error;
        }
    },

    login: async (email, password) => {
        try {
            const response = await authenticatedAPI.post('customer/auth/login', { email, password });
            return response.data;
        } catch (error) {
            console.error("Error logging in:", error);
            throw error;
        }
    },

    getCurrentUser: async () => {
        try {
            const response = await authenticatedAPI.get('customer/auth/me');
            return response.data;
        } catch (error) {
            console.error("Error fetching current user:", error);
            throw error;
        }
    },
};

// ==================== CUSTOMER ORDERS API ====================
export const CustomerOrdersAPI = {
    getCart: async () => {
        try {
            const response = await authenticatedAPI.get('customer/orders/cart');
            return response.data;
        } catch (error) {
            console.error("Error fetching cart:", error);
            throw error;
        }
    },

    addCartItem: async (productId, quantity) => {
        try {
            const response = await authenticatedAPI.post('customer/orders/cart/items', {
                productID: productId,
                quantity: quantity
            });
            return response.data;
        } catch (error) {
            console.error("Error adding item to cart:", error);
            throw error;
        }
    },

    updateCartItem: async (itemId, quantity) => {
        try {
            const response = await authenticatedAPI.put(`customer/orders/cart/items/${itemId}`, {
                quantity: quantity
            });
            return response.data;
        } catch (error) {
            console.error("Error updating cart item:", error);
            throw error;
        }
    },

    removeCartItem: async (itemId) => {
        try {
            await authenticatedAPI.delete(`customer/orders/cart/items/${itemId}`);
            return true;
        } catch (error) {
            console.error("Error removing cart item:", error);
            throw error;
        }
    },

    createOrder: async (orderData) => {
        try {
            const response = await authenticatedAPI.post('customer/orders/orders', orderData);
            return response.data;
        } catch (error) {
            console.error("Error creating order:", error);
            throw error;
        }
    },

    getOrders: async () => {
        try {
            const response = await authenticatedAPI.get('customer/orders/orders');
            return response.data;
        } catch (error) {
            console.error("Error fetching orders:", error);
            throw error;
        }
    },

    getOrderById: async (id) => {
        try {
            const response = await authenticatedAPI.get(`customer/orders/orders/${id}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching order:", error);
            throw error;
        }
    },
};

// ==================== ADMIN PRODUCTS API ====================
export const AdminProductsAPI = {
    getAllProducts: async (page = 1, pageSize = 20, search = null, categoryId = null, isPublished = null) => {
        try {
            let url = `admin/products?page=${page}&pageSize=${pageSize}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (categoryId) url += `&categoryId=${categoryId}`;
            if (isPublished !== null) url += `&isPublished=${isPublished}`;
            const response = await authenticatedAPI.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching products:", error);
            throw error;
        }
    },

    getProductById: async (id) => {
        try {
            const response = await authenticatedAPI.get(`admin/products/${id}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching product:", error);
            throw error;
        }
    },

    createProduct: async (productData) => {
        try {
            console.log("API: Creating product with data:", productData);
            console.log("API: CategoryID:", productData.categoryID, "Type:", typeof productData.categoryID);
            const response = await authenticatedAPI.post('admin/products', productData);
            return response.data;
        } catch (error) {
            console.error("Error creating product:", error);
            console.error("Request payload was:", productData);
            if (error.response) {
                console.error("Error response:", error.response.data);
            }
            throw error;
        }
    },

    updateProduct: async (id, productData) => {
        try {
            console.log("API: Updating product with data:", productData);
            console.log("API: CategoryID:", productData.categoryID, "Type:", typeof productData.categoryID);
            const response = await authenticatedAPI.put(`admin/products/${id}`, productData);
            return response.data;
        } catch (error) {
            console.error("Error updating product:", error);
            console.error("Request payload was:", productData);
            if (error.response) {
                console.error("Error response:", error.response.data);
            }
            throw error;
        }
    },

    deleteProduct: async (id) => {
        try {
            await authenticatedAPI.delete(`admin/products/${id}`);
            return true;
        } catch (error) {
            console.error("Error deleting product:", error);
            throw error;
        }
    },

    publishProduct: async (id, isPublished) => {
        try {
            const response = await authenticatedAPI.put(`admin/products/${id}/publish`, { isPublished });
            return response.data;
        } catch (error) {
            console.error("Error publishing product:", error);
            throw error;
        }
    },

    updateStock: async (id, quantity) => {
        try {
            const response = await authenticatedAPI.put(`admin/products/${id}/stock`, { quantity });
            return response.data;
        } catch (error) {
            console.error("Error updating stock:", error);
            throw error;
        }
    },

    getLowStockProducts: async () => {
        try {
            const response = await authenticatedAPI.get('admin/products/low-stock');
            return response.data;
        } catch (error) {
            console.error("Error fetching low stock products:", error);
            throw error;
        }
    },

    // Product Image Management
    uploadProductImage: async (file) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await authenticatedAPI.post('admin/products/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response?.data;
        } catch (error) {
            console.error("Error uploading product image:", error);
            throw error;
        }
    },

    getProductImages: async (productId) => {
        try {
            const response = await authenticatedAPI.get(`admin/products/${productId}/images`);
            return response.data;
        } catch (error) {
            console.error("Error fetching product images:", error);
            throw error;
        }
    },

    addProductImage: async (productId, imageData) => {
        try {
            const response = await authenticatedAPI.post(`admin/products/${productId}/images`, imageData);
            return response.data;
        } catch (error) {
            console.error("Error adding product image:", error);
            throw error;
        }
    },

    updateProductImage: async (productId, imageId, imageData) => {
        try {
            const response = await authenticatedAPI.put(`admin/products/${productId}/images/${imageId}`, imageData);
            return response.data;
        } catch (error) {
            console.error("Error updating product image:", error);
            throw error;
        }
    },

    deleteProductImage: async (productId, imageId) => {
        try {
            await authenticatedAPI.delete(`admin/products/${productId}/images/${imageId}`);
            return true;
        } catch (error) {
            console.error("Error deleting product image:", error);
            throw error;
        }
    },

    setPrimaryImage: async (productId, imageId) => {
        try {
            const response = await authenticatedAPI.put(`admin/products/${productId}/images/${imageId}/set-primary`);
            return response.data;
        } catch (error) {
            console.error("Error setting primary image:", error);
            throw error;
        }
    },
};

// ==================== ADMIN CATEGORIES API ====================
export const AdminCategoriesAPI = {
    getAllCategories: async () => {
        try {
            const response = await authenticatedAPI.get('admin/categories');
            return response.data;
        } catch (error) {
            console.error("Error fetching categories:", error);
            throw error;
        }
    },

    getCategoryById: async (id) => {
        try {
            const response = await authenticatedAPI.get(`admin/categories/${id}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching category:", error);
            throw error;
        }
    },

    createCategory: async (categoryData) => {
        try {
            const response = await authenticatedAPI.post('admin/categories', categoryData);
            return response.data;
        } catch (error) {
            console.error("Error creating category:", error);
            throw error;
        }
    },

    updateCategory: async (id, categoryData) => {
        try {
            const response = await authenticatedAPI.put(`admin/categories/${id}`, categoryData);
            return response.data;
        } catch (error) {
            console.error("Error updating category:", error);
            throw error;
        }
    },

    deleteCategory: async (id) => {
        try {
            await authenticatedAPI.delete(`admin/categories/${id}`);
            return true;
        } catch (error) {
            console.error("Error deleting category:", error);
            throw error;
        }
    },

    getRootCategories: async () => {
        try {
            const response = await authenticatedAPI.get('admin/categories/root');
            return response.data;
        } catch (error) {
            console.error("Error fetching root categories:", error);
            throw error;
        }
    },

    getSubCategories: async (parentId) => {
        try {
            const response = await authenticatedAPI.get(`admin/categories/${parentId}/subcategories`);
            return response.data;
        } catch (error) {
            console.error("Error fetching subcategories:", error);
            throw error;
        }
    },
};

// ==================== ADMIN ORDERS API ====================
export const AdminOrdersAPI = {
    getAllOrders: async (page = 1, pageSize = 20, search = null, statusId = null, fromDate = null, toDate = null) => {
        try {
            let url = `admin/orders?page=${page}&pageSize=${pageSize}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (statusId) url += `&statusId=${statusId}`;
            if (fromDate) url += `&fromDate=${fromDate}`;
            if (toDate) url += `&toDate=${toDate}`;
            const response = await authenticatedAPI.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching orders:", error);
            throw error;
        }
    },

    getOrderById: async (id) => {
        try {
            const response = await authenticatedAPI.get(`admin/orders/${id}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching order:", error);
            throw error;
        }
    },

    getOrderByNumber: async (orderNumber) => {
        try {
            const response = await authenticatedAPI.get(`admin/orders/number/${orderNumber}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching order by number:", error);
            throw error;
        }
    },

    updateOrderStatus: async (id, statusId, trackingNumber = null) => {
        try {
            const response = await authenticatedAPI.put(`admin/orders/${id}/status`, {
                statusID: statusId,
                trackingNumber: trackingNumber
            });
            return response.data;
        } catch (error) {
            console.error("Error updating order status:", error);
            throw error;
        }
    },

    updateTracking: async (id, trackingNumber, carrier = null) => {
        try {
            const response = await authenticatedAPI.put(`admin/orders/${id}/tracking`, {
                trackingNumber: trackingNumber,
                carrier: carrier
            });
            return response.data;
        } catch (error) {
            console.error("Error updating tracking:", error);
            throw error;
        }
    },

    getOrderStatuses: async () => {
        try {
            const response = await authenticatedAPI.get('admin/orders/statuses');
            return response.data;
        } catch (error) {
            console.error("Error fetching order statuses:", error);
            throw error;
        }
    },
};

// ==================== ADMIN DASHBOARD API ====================
export const AdminDashboardAPI = {
    getStats: async (fromDate = null, toDate = null) => {
        try {
            let url = 'admin/dashboard/stats';
            if (fromDate) url += `?fromDate=${fromDate}`;
            if (toDate) url += `${fromDate ? '&' : '?'}toDate=${toDate}`;
            const response = await authenticatedAPI.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
            throw error;
        }
    },

    getRecentOrders: async (count = 10) => {
        try {
            const response = await authenticatedAPI.get(`admin/dashboard/recent-orders?count=${count}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching recent orders:", error);
            throw error;
        }
    },

    getTopProducts: async (count = 10, fromDate = null, toDate = null) => {
        try {
            let url = `admin/dashboard/top-products?count=${count}`;
            if (fromDate) url += `&fromDate=${fromDate}`;
            if (toDate) url += `&toDate=${toDate}`;
            const response = await authenticatedAPI.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching top products:", error);
            throw error;
        }
    },

    getSalesChart: async (fromDate = null, toDate = null, groupBy = 'day') => {
        try {
            let url = `admin/dashboard/sales-chart?groupBy=${groupBy}`;
            if (fromDate) url += `&fromDate=${fromDate}`;
            if (toDate) url += `&toDate=${toDate}`;
            const response = await authenticatedAPI.get(url);
            return response.data;
        } catch (error) {
            console.error("Error fetching sales chart:", error);
            throw error;
        }
    },
};
